import type { CSSProperties } from "react";
import type { AuditLogEntry } from "../../lib/api";
import { describeRecipeHistory, historyRecord } from "../../lib/recipeHistory";

interface Props {
  entries: AuditLogEntry[];
  loading: boolean;
}

const ACTION_LABELS: Record<string, string> = {
  create_drink: "Drink created",
  create_draft_from_approved: "Draft created from Approved",
  restore_as_draft: "Restored from previous version",
  update_draft: "Draft edited",
  submit_for_review: "Submitted for review",
  discard_draft: "Draft discarded",
  approve_version: "Approved & published",
  retire_version: "Retired",
  set_service_status: "Service status changed",
  update_aliases: "Search aliases changed",
};

const ACTION_TONE: Record<string, "approved" | "muted" | "red"> = {
  create_drink: "muted",
  create_draft_from_approved: "muted",
  restore_as_draft: "muted",
  update_draft: "muted",
  submit_for_review: "muted",
  discard_draft: "red",
  approve_version: "approved",
  retire_version: "red",
};

export default function ChangeHistoryList({ entries, loading }: Props) {
  if (loading) return <p style={styles.muted}>Loading change history…</p>;
  if (entries.length === 0) {
    return <p style={styles.muted}>No history yet.</p>;
  }

  // The history endpoint returns newest first. Keep that entry visible while
  // older changes remain available without filling the recipe page.
  return (
    <div>
      <HistoryEntries entries={entries.slice(0, 1)} />
      {entries.length > 1 && (
        <details key={entries[0].id} style={{ marginTop: 8 }}>
          <summary style={styles.expandHistory}>
            Older changes ({entries.length - 1})
          </summary>
          <HistoryEntries entries={entries.slice(1)} />
        </details>
      )}
    </div>
  );
}

function HistoryEntries({ entries }: { entries: AuditLogEntry[] }) {
  return (
    <ol style={styles.list}>
      {entries.map((e) => {
        const label = ACTION_LABELS[e.action] ?? e.action;
        const tone = ACTION_TONE[e.action] ?? "muted";
        const details = describeRecipeHistory(e);
        const after = historyRecord(e.after_data);
        const before = historyRecord(e.before_data);
        const versionRef =
          after.new_approved_id ??
          after.new_version_id ??
          e.entity_id;
        const versionNum =
          after.new_version_number ?? before.version_number ?? null;
        const hasVersionNum = typeof versionNum === "string" || (typeof versionNum === "number" && Number.isFinite(versionNum));
        return (
          <li key={e.id} style={styles.row}>
            <div style={styles.rowTop}>
              <span style={{ ...styles.actionChip, ...chipTone[tone] }}>{label}</span>
              {hasVersionNum && (
                <span style={styles.versionTag}>v{String(versionNum)}</span>
              )}
              <span style={styles.timestamp}>
                {new Date(e.created_at).toLocaleString()}
              </span>
            </div>
            <div style={styles.rowMeta}>
              {e.user_name ? <strong>{e.user_name}</strong> : "(system)"}
              {typeof versionRef === "string" && versionRef !== e.entity_id && (
                <span style={styles.subtle}> · ref {versionRef.slice(0, 8)}</span>
              )}
            </div>
            {details.changes.length > 0 && (
              <ul style={styles.changes}>
                {details.changes.map((change, index) => (
                  <li key={`${change.field}-${index}`} style={styles.change}>
                    <strong>{change.label}</strong>
                    <div style={styles.changeValue}><span style={styles.valueLabel}>Before: </span>{change.before === null || change.before === "" ? "Not set" : change.before}</div>
                    <div style={styles.changeValue}><span style={styles.valueLabel}>After: </span>{change.after === null || change.after === "" ? "Not set" : change.after}</div>
                  </li>
                ))}
              </ul>
            )}
            {details.message && <p style={styles.detailMessage}>{details.message}</p>}
            {details.note && <p style={styles.summary}><strong>Note: </strong>{details.note}</p>}
          </li>
        );
      })}
    </ol>
  );
}

const chipTone: Record<string, CSSProperties> = {
  approved: { background: "rgba(39,174,96,0.16)", color: "#86efac", borderColor: "rgba(39,174,96,0.3)" },
  muted:    { background: "var(--bg)",            color: "var(--muted)", borderColor: "var(--border)" },
  red:      { background: "rgba(224,80,64,0.16)", color: "#FF8A7A", borderColor: "rgba(224,80,64,0.4)" },
};

const styles: Record<string, CSSProperties> = {
  expandHistory: {
    minHeight: 48,
    padding: "14px 10px",
    marginBottom: 8,
    border: "1px solid var(--border)",
    borderRadius: 10,
    color: "var(--gold)",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  list: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 },
  row: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
  },
  rowTop: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 4,
    flexWrap: "wrap",
  },
  actionChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: 999,
    border: "1px solid",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  versionTag: {
    fontSize: 11,
    fontWeight: 800,
    color: "var(--text)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  timestamp: {
    marginLeft: "auto",
    fontSize: 10,
    color: "var(--muted)",
    fontWeight: 600,
  },
  rowMeta: { fontSize: 11, color: "var(--muted)", lineHeight: 1.3 },
  subtle: { color: "var(--muted)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
  summary: {
    margin: "6px 0 0",
    fontSize: 14,
    color: "var(--text)",
    lineHeight: 1.35,
    fontStyle: "italic",
    overflowWrap: "anywhere",
    whiteSpace: "pre-wrap",
  },
  changes: { listStyle: "none", padding: 0, margin: "10px 0 0", display: "grid", gap: 10 },
  change: { minWidth: 0, fontSize: 15, lineHeight: 1.5, overflowWrap: "anywhere", borderTop: "1px solid var(--border)", paddingTop: 8 },
  changeValue: { whiteSpace: "pre-wrap", marginTop: 3 },
  valueLabel: { color: "var(--muted)", fontSize: 14 },
  detailMessage: { margin: "8px 0 0", fontSize: 14, lineHeight: 1.5, color: "var(--muted)" },
  muted: { color: "var(--muted)", fontSize: 13, padding: 8 },
};
